import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface DeliveryMapProps {
  customerAddress: string;
  customerLocation?: { lat: number; lng: number };
  orderStatus: string;
  orderId: string;
}

// Component to fit bounds
const FitBounds: React.FC<{ locations: Array<{ lat: number; lng: number }> }> = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
};

const DeliveryMap: React.FC<DeliveryMapProps> = ({
  customerAddress,
  customerLocation,
  orderStatus,
  orderId
}) => {
  const [myLocation, setMyLocation] = useState<[number, number] | null>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<Array<[number, number]>>([]);
  const [distance, setDistance] = useState<string>('');
  const [duration, setDuration] = useState<string>('');

  // Get partner's current location
  useEffect(() => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setMyLocation([position.coords.latitude, position.coords.longitude]);
        },
        (error) => console.error('Location error:', error),
        { enableHighAccuracy: true, maximumAge: 10000 }
      );

      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  // Calculate route when both locations are available
  useEffect(() => {
    if (myLocation && customerLocation) {
      // Simple straight line
      setRouteCoordinates([myLocation, [customerLocation.lat, customerLocation.lng]]);
      
      // Calculate distance
      const dist = calculateDistance(
        myLocation[0], myLocation[1],
        customerLocation.lat, customerLocation.lng
      );
      setDistance(dist.toFixed(2));
      setDuration(Math.ceil(dist * 3).toString()); // ~3 min per km estimate
    }
  }, [myLocation, customerLocation]);

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Custom icons
  const partnerIcon = new L.DivIcon({
    html: `<div style="background: #4CAF50; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 20px;">🛵</div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  const customerIcon = new L.DivIcon({
    html: `<div style="background: #FF6F61; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 20px;">📍</div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  // Default location (Hyderabad)
  const defaultCenter: [number, number] = customerLocation 
    ? [customerLocation.lat, customerLocation.lng]
    : myLocation || [17.385044, 78.486671];

  const locations = [];
  if (myLocation) locations.push({ lat: myLocation[0], lng: myLocation[1] });
  if (customerLocation) locations.push(customerLocation);

  return (
    <div style={{ marginTop: '15px' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>

      {/* Stats */}
      {distance && (
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '10px'
        }}>
          <div style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            color: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{distance} km</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Distance</div>
          </div>
          <div style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #FF6F61 0%, #FF9B96 100%)',
            color: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{duration} min</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Est. Time</div>
          </div>
        </div>
      )}

      {/* Map */}
      <div style={{
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
      }}>
        <MapContainer
          center={defaultCenter}
          zoom={14}
          style={{ height: '350px', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {locations.length > 0 && <FitBounds locations={locations} />}

          {/* Partner's current location */}
          {myLocation && (
            <Marker position={myLocation} icon={partnerIcon} />
          )}

          {/* Customer location */}
          {customerLocation && (
            <Marker
              position={[customerLocation.lat, customerLocation.lng]}
              icon={customerIcon}
            />
          )}

          {/* Route */}
          {routeCoordinates.length > 0 && (
            <Polyline
              positions={routeCoordinates}
              pathOptions={{
                color: '#4CAF50',
                weight: 4,
                opacity: 0.7
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* Address */}
      <div style={{
        marginTop: '12px',
        padding: '12px',
        background: '#f5f5f5',
        borderRadius: '8px',
        borderLeft: '4px solid #FF6F61'
      }}>
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
          📍 Delivery Address:
        </div>
        <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
          {customerAddress}
        </div>
      </div>

      {/* Status */}
      <div style={{
        marginTop: '10px',
        padding: '10px',
        background: 'linear-gradient(135deg, #fff5ed 0%, #fffaf5 100%)',
        borderRadius: '8px',
        textAlign: 'center',
        fontSize: '14px',
        color: '#666'
      }}>
        {!myLocation && '📍 Enable location to see navigation'}
        {myLocation && !customerLocation && '⏳ Customer location not available'}
        {myLocation && customerLocation && '✅ Navigate to customer using the map'}
      </div>
    </div>
  );
};

export default DeliveryMap;
